/**
 * Chart colour.
 *
 * ---------------------------------------------------------------------------
 * THE CONSTRAINT, MEASURED
 *
 * Digital Codex has three brand hues: purple (#743EA0), blue (#13367E) and
 * coral (#E75D5B). Purple and blue are neighbours on the colour wheel, and the
 * coral/rose pair are near-identical. Running the palette through pairwise
 * OKLab separation (ΔE ×100) against a light surface:
 *
 *   purple  vs coral  → 27.1 normal / 21.6 protan    PASS
 *   purple  vs blue   → 10.5 normal /  2.0 protan    FAIL
 *   coral   vs rose   →  3.0 normal /  2.4 protan    FAIL
 *   blue    vs blue-2 →  8.3 normal                  FAIL
 *
 * A red-green colourblind reader cannot separate purple from blue at all
 * (ΔE 2.0 — effectively the same colour), and *nobody* can separate the two
 * corals (ΔE 3.0). So the naive six-colour brand ramp produces charts that
 * look on-brand and cannot be read.
 *
 * The brand therefore supports TWO reliable categorical series. This module
 * encodes that honestly instead of pretending otherwise.
 *
 * WHAT TO DO WHEN YOU NEED MORE THAN TWO
 *   - Most admin charts are single-series (orders over time) or ranked bars
 *     (top brands), where colour carries no identity at all. Use `chartSeries`
 *     and stop.
 *   - Three series: `CHART_SERIES[2]` is available but MUST be paired with
 *     direct labels — colour alone will not distinguish it.
 *   - Four or more: do not invent hues. Fold the tail into "Other", or split
 *     into small multiples. A generated 7th colour is unreadable by
 *     construction.
 *   - Success/failure, or any status comparison: use `STATUS_SERIES`. Semantics
 *     beat brand — failed orders are red, not "series two" (§60).
 *
 * Values are read from CSS variables so charts follow the active theme
 * automatically; the dark steps are separately validated, not flipped.
 * ------------------------------------------------------------------------- */

/** Categorical series, in fixed assignment order. Never cycled. */
export const CHART_SERIES = [
  'var(--chart-1)', // brand purple
  'var(--chart-2)', // brand coral
  'var(--chart-3)', // brand blue — requires direct labels
] as const;

/** Maximum series that colour alone can distinguish. */
export const MAX_CATEGORICAL_SERIES = 2;

export const STATUS_SERIES = {
  success: 'var(--chart-success)',
  danger: 'var(--chart-danger)',
} as const;

/** Recessive furniture, so the data stays the loudest thing on screen. */
export const CHART_GRID = 'var(--border)';
export const CHART_AXIS_TEXT = 'var(--muted-foreground)';

/**
 * Colour for series `index`.
 *
 * Clamps rather than wrapping: wrapping would silently paint series 3 and
 * series 0 identically, which is worse than repeating the last colour and is
 * much harder to notice in review.
 */
export function chartSeries(index: number): string {
  return CHART_SERIES[Math.min(index, CHART_SERIES.length - 1)] as string;
}
